import { useRef, useState, type ChangeEvent, type FormEvent, type KeyboardEvent } from 'react';
import { Paperclip, SendHorizontal, X } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { getErrorMessage } from '@/lib/api-error';
import { prepararFotoChat, validateImageFile } from '@/lib/image-file';
import type { EnviarImagenRequest } from '@/types/conversations';
import {
  MENSAJE_CAPTION_MAX,
  MENSAJE_IMAGEN_MAX_BYTES,
  MENSAJE_MAX_CHARS,
  composerText,
  describeCaptionLength,
  describeMensajeLength,
  mostrarContadorDeMensaje,
  normalizeMensaje,
  puedeEnviarCaption,
  puedeEnviarMensaje,
} from './inbox-format';

/**
 * Composer del panel: el asesor escribe **texto o una foto** y responde a mano (módulo 5).
 *
 * Es presentacional: no habla con la API. El panel le pasa las dos mutaciones (`onSend` y
 * `onSendImage`, ver `use-send-message.ts` / `use-send-image.ts`) y su estado (`sending`/`error`). Así
 * el mismo composer se puede probar en el smoke sin navegador.
 *
 * Decisiones que se ven en el comportamiento:
 *  - **el texto (y la foto) se limpian solo si el envío salió bien**: las dos props devuelven una
 *    promesa y, si el backend responde 502 (por ejemplo, la ventana de 24 h cerrada), lo que se
 *    escribió sigue en el campo para que nadie tenga que redactarlo otra vez;
 *  - **la foto se prepara ANTES de enviarla** (validar el archivo y reescalarlo a 1600 px en el
 *    navegador, `prepararFotoChat`): un archivo de 8 MB no se lee para nada y el que viaja es el
 *    comprimido;
 *  - **con una foto adjunta, el texto pasa a ser el PIE de foto** y su tope baja a
 *    `MENSAJE_CAPTION_MAX` (el de WhatsApp para captions), así que el campo y el contador cambian;
 *  - **la foto adjunta se quita con la X** (o al enviarse), pero si algo falla se queda: el asesor
 *    puede reintentar o quitarla y mandar solo el texto;
 *  - **Enter envía y Shift + Enter salta de línea**, como en cualquier chat.
 *
 * Lo que NO hace: bloquear el campo. No hay forma barata de saber desde acá si el canal está
 * conectado, si la ventana de 24 h sigue abierta o si el backend tiene `PUBLIC_BASE_URL`; dejar al
 * asesor sin poder escribir por una suposición es peor que enviar y leer el motivo en el aviso.
 */

/** La foto ya preparada, lista para mandar. */
interface FotoAdjunta {
  base64: string;
  mimeType: string | null;
  /** Data URL para la miniatura: no se sube nada hasta que se envía. */
  preview: string;
  nombre: string;
}

export interface MessageComposerProps {
  /** Nombre del contacto, para el `aria-label` del campo. */
  contacto: string;
  /** Hay un envío en curso, de texto o de foto (bloquea el campo, el adjunto y el botón). */
  sending: boolean;
  /** Error del último envío; el mensaje del backend se pinta tal cual. */
  error: unknown;
  /**
   * Envía el texto. Devuelve una promesa para que el composer sepa si puede limpiar el campo:
   * se limpia al resolverse y se deja intacto si falla.
   */
  onSend: (contenido: string) => Promise<unknown>;
  /** Envía la foto (con el texto como pie, si hay). Misma regla: la promesa decide el limpiado. */
  onSendImage: (imagen: EnviarImagenRequest) => Promise<unknown>;
}

export function MessageComposer({
  contacto,
  sending,
  error,
  onSend,
  onSendImage,
}: MessageComposerProps) {
  const [texto, setTexto] = useState('');
  const [foto, setFoto] = useState<FotoAdjunta | null>(null);
  /** Error de la preparación de la foto (validación del cliente); el del envío llega por `error`. */
  const [fotoError, setFotoError] = useState<string | null>(null);
  /** Cambiar la `key` permite volver a elegir el MISMO archivo después de quitarlo. */
  const [fileInputKey, setFileInputKey] = useState(0);
  const [preparando, setPreparando] = useState(false);

  const fileInput = useRef<HTMLInputElement>(null);

  const limpio = normalizeMensaje(texto);
  const ocupado = sending || preparando;
  const contador = mostrarContadorDeMensaje(texto);
  const puede = foto
    ? !ocupado && puedeEnviarCaption(texto)
    : puedeEnviarMensaje(texto) && !ocupado;

  async function handleFile(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    setFileInputKey((key) => key + 1);
    if (!file) return;

    // Se valida ANTES de leer: un archivo demasiado grande ni se convierte a base64.
    const fileError = validateImageFile(file, MENSAJE_IMAGEN_MAX_BYTES);
    if (fileError) {
      setFotoError(fileError);
      return;
    }

    setFotoError(null);
    setPreparando(true);

    try {
      const { base64, mimeType } = await prepararFotoChat(file);
      const tipo = mimeType ?? (file.type || 'image/jpeg').toLowerCase();
      setFoto({ base64, mimeType, preview: `data:${tipo};base64,${base64}`, nombre: file.name });
    } catch (err) {
      setFotoError(getErrorMessage(err));
    } finally {
      setPreparando(false);
    }
  }

  const enviar = (): void => {
    if (!puede) return;

    // Con foto adjunta el texto es el pie; sin ella, el mensaje es el texto.
    const promesa = foto
      ? onSendImage({
          imageBase64: foto.base64,
          ...(foto.mimeType ? { mimeType: foto.mimeType } : {}),
          ...(limpio.length > 0 ? { caption: limpio } : {}),
        })
      : onSend(limpio);

    void Promise.resolve(promesa)
      .then(() => {
        setTexto('');
        setFoto(null);
        setFotoError(null);
      })
      .catch(() => undefined);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    enviar();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== 'Enter' || event.shiftKey) return;

    event.preventDefault();
    enviar();
  };

  return (
    <form className="border-t border-line px-6 py-4" onSubmit={handleSubmit}>
      {error ? <Alert className="mb-3">{getErrorMessage(error)}</Alert> : null}
      {fotoError ? <Alert className="mb-3">{fotoError}</Alert> : null}

      {foto ? (
        <div className="mb-3 flex items-center gap-3 rounded-panel border border-line bg-surface-muted px-3 py-2">
          <img
            src={foto.preview}
            alt=""
            className="size-12 shrink-0 rounded-control object-cover"
          />

          <div className="min-w-0 flex-1">
            <p className="truncate text-caption font-semibold text-ink">{foto.nombre}</p>
            <p className="text-micro text-ink-muted">{composerText.fotoAyuda}</p>
          </div>

          <Button
            variant="ghost"
            size="icon"
            aria-label={composerText.quitarFoto}
            disabled={ocupado}
            onClick={() => {
              setFoto(null);
              setFotoError(null);
            }}
          >
            <X size={16} />
          </Button>
        </div>
      ) : null}

      <div className="flex items-end gap-2">
        {/* El selector va oculto y lo dispara el clip: un input de archivo a la vista al lado del
            campo de texto rompería la fila (es el mismo patrón que las fotos del catálogo). */}
        <input
          key={fileInputKey}
          ref={fileInput}
          type="file"
          accept="image/*"
          className="hidden"
          aria-label={composerText.adjuntar}
          disabled={ocupado}
          onChange={(event) => void handleFile(event)}
        />

        <Button
          type="button"
          variant="secondary"
          size="icon"
          aria-label={composerText.adjuntar}
          disabled={ocupado}
          onClick={() => fileInput.current?.click()}
        >
          <Paperclip size={16} />
        </Button>

        <Textarea
          className="min-h-11 resize-none"
          rows={1}
          value={texto}
          maxLength={foto ? MENSAJE_CAPTION_MAX : MENSAJE_MAX_CHARS}
          placeholder={foto ? composerText.placeholderPie : composerText.placeholder}
          aria-label={`Escribir un mensaje a ${contacto}`}
          disabled={sending}
          onChange={(event) => setTexto(event.target.value)}
          onKeyDown={handleKeyDown}
        />

        <Button
          type="submit"
          loading={sending}
          disabled={!puede}
          leftIcon={<SendHorizontal size={16} />}
        >
          {composerText.enviar}
        </Button>
      </div>

      <p className="mt-1.5 flex items-start justify-between gap-3 text-micro text-ink-faint">
        <span className="min-w-0">{foto ? composerText.avisoFoto : composerText.aviso}</span>

        {foto ? (
          <span className="shrink-0 tabular-nums">{describeCaptionLength(texto)}</span>
        ) : contador ? (
          <span className="shrink-0 tabular-nums">{describeMensajeLength(texto)}</span>
        ) : (
          <span className="shrink-0">{composerText.ayudaTeclado}</span>
        )}
      </p>
    </form>
  );
}
